import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { ATTENDANCE_STATUS_CONFIG } from "../../Moduels/attendance/indianCalendarUtils";
import CompOffGrantDialog from "./CompOffGrantDialog";

const EDITABLE_STATUSES = ["P", "A", "HD", "L"];
const WORKED_STATUSES = ["P", "HD"];

export default function AttendanceChangeModal({
  day,
  dateLabel,
  currentStatus,
  leaveTypes = [],
  currentLeaveType = "",
  compOffAvailable = 0,
  compOffOffer = null,
  hint = "",
  onSave,
  onCancel,
}) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || "P");
  const [selectedLeaveType, setSelectedLeaveType] = useState(currentLeaveType || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [askingCompOff, setAskingCompOff] = useState(false);

  useEffect(() => {
    setSelectedStatus(currentStatus || "P");
    setSelectedLeaveType(currentLeaveType || "");
    setError("");
    setSaving(false);
    setSuccess(false);
    setAskingCompOff(false);
  }, [day, currentStatus, currentLeaveType]);

  const handleSave = async (compOffDecision) => {
    if (selectedStatus === "L" && !selectedLeaveType) {
      setError("Please select a leave type.");
      return;
    }
    const choice = compOffDecision === "allow" || compOffDecision === "skip" ? compOffDecision : undefined;
    if (!choice && compOffOffer && WORKED_STATUSES.includes(selectedStatus)) {
      setAskingCompOff(true);
      return;
    }
    setAskingCompOff(false);
    setSaving(true);
    setError("");
    try {
      await onSave(
        selectedStatus,
        selectedStatus === "L" ? selectedLeaveType : "",
        choice
      );
      setSaving(false);
      setSuccess(true);
      setTimeout(() => {
        onCancel();
      }, 1000);
    } catch (err) {
      setSaving(false);
      setError(err?.message || "Failed to save. Please try again.");
    }
  };

  const disabled = saving || success;

  return createPortal(
    <>
    {askingCompOff && compOffOffer ? (
      <CompOffGrantDialog
        dayKind={compOffOffer.dayKind}
        holidayName={compOffOffer.holidayName}
        dateLabel={compOffOffer.dateLabel || dateLabel}
        employeeName={compOffOffer.employeeName}
        creditLabel={compOffOffer.creditLabel}
        busy={saving}
        onAllow={() => handleSave("allow")}
        onSkip={() => handleSave("skip")}
        onCancel={() => { if (!saving) setAskingCompOff(false); }}
      />
    ) : null}
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => { if (e.target === e.currentTarget && !disabled) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-teal-50/40">
          <div>
            <h3 className="text-base font-bold text-slate-900">Change Attendance</h3>
            <p className="text-sm font-medium text-slate-700 mt-0.5">Day {day} &mdash; {dateLabel}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="px-5 py-8 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-bold text-emerald-700">Saved successfully</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-2">
              {hint ? (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  {hint}
                </p>
              ) : null}
              {EDITABLE_STATUSES.map((code) => {
                const cfg = ATTENDANCE_STATUS_CONFIG[code];
                if (!cfg) return null;
                const checked = selectedStatus === code;
                return (
                  <label
                    key={code}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      checked
                        ? "border-teal-400 bg-teal-50/60 shadow-sm"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    } ${saving ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <input
                      type="radio"
                      name="att-status"
                      value={code}
                      checked={checked}
                      disabled={saving}
                      onChange={() => { setSelectedStatus(code); setError(""); }}
                      className="accent-teal-600 w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-slate-800">{cfg.label}</span>
                  </label>
                );
              })}

              {selectedStatus === "L" && (
                <div className="pl-3 pt-1">
                  <label className="block text-xs font-semibold text-slate-600 tracking-wide mb-1.5">
                    Select Leave Type
                  </label>
                  <select
                    value={selectedLeaveType}
                    disabled={saving}
                    onChange={(e) => { setSelectedLeaveType(e.target.value); setError(""); }}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 outline-none disabled:opacity-60"
                  >
                    <option value="">— Choose leave type —</option>
                    {leaveTypes.map((lt) => (
                      <option key={lt.code} value={lt.code}>
                        {String(lt.code).toUpperCase() === "CO" ? lt.name : `${lt.code} — ${lt.name}`}
                      </option>
                    ))}
                  </select>
                  {String(selectedLeaveType).toUpperCase() === "CO" ? (
                    <p className="mt-1.5 text-xs text-amber-800">
                      This uses 1 day from the available Comp Off balance ({compOffAvailable}).
                    </p>
                  ) : null}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle size={14} className="shrink-0" />
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-md transition disabled:opacity-70 flex items-center gap-2"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    </>,
    document.body
  );
}

export function AttendanceInfoPopover({ type, holidayName, onClose }) {
  const isHoliday = type === "H";
  const title = isHoliday ? "Holiday" : "Week Off";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs mx-4 overflow-hidden animate-in fade-in zoom-in-95">
        <div className={`px-5 py-4 ${isHoliday ? "bg-violet-50" : "bg-slate-50"}`}>
          <h3 className={`text-base font-bold ${isHoliday ? "text-violet-900" : "text-slate-900"}`}>
            {title}
          </h3>
          {isHoliday && holidayName && (
            <p className="text-sm font-semibold text-violet-700 mt-1">{holidayName}</p>
          )}
        </div>
        <div className="px-5 py-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            {isHoliday
              ? "This holiday comes from the organisation calendar. Mark the employee Present if they worked, and you can add the day to their Comp Off balance."
              : "This week off comes from the organisation calendar. Mark the employee Present if they worked, and you can add the day to their Comp Off balance."}
          </p>
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
